import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
/* src/components/GroupFour.jsx
   IB Group 4 — Sciences */
import React, { useState, useEffect, useMemo } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import { getEntryForSubject } from "../utils/timezone";
import { getBoundaryResolutionOrder } from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const COMPONENTS = {
  "Astronomy_SL": [["p1", "Paper 1", 30, "30%"], ["p2", "Paper 2", 40, "50%"], ["ia", "IA", 30, "20%"]],
  "Biology_SL": [["p1", "Paper 1", 55, "36%"], ["p2", "Paper 2", 50, "44%"], ["ia", "IA", 24, "20%"]],
  "Biology_HL": [["p1", "Paper 1", 75, "36%"], ["p2", "Paper 2", 80, "44%"], ["ia", "IA", 24, "20%"]],
  "Chemistry_SL": [["p1", "Paper 1", 55, "36%"], ["p2", "Paper 2", 50, "44%"], ["ia", "IA", 24, "20%"]],
  "Chemistry_HL": [["p1", "Paper 1", 75, "36%"], ["p2", "Paper 2", 90, "44%"], ["ia", "IA", 24, "20%"]],
  "Computer Science_SL": [["p1", "Paper 1", 70, "45%"], ["p2", "Paper 2", 45, "25%"], ["sol", "Solution", 34, "30%"]],
  "Computer Science_HL": [["p1", "Paper 1", 100, "40%"], ["p2", "Paper 2", 65, "20%"], ["p3", "Paper 3", 30, "20%"], ["sol", "Solution", 34, "20%"]],
  "Design Technology_SL": [["p1", "Paper 1", 30, "30%"], ["p2", "Paper 2", 50, "30%"], ["ia", "Design Proj.", 36, "40%"]],
  "Design Technology_HL": [["p1", "Paper 1", 40, "20%"], ["p2", "Paper 2", 50, "20%"], ["p3", "Paper 3", 40, "20%"], ["ia", "Design Proj.", 54, "40%"]],
  "Environmental Systems and Societies_SL": [["p1", "Paper 1", 35, "25%"], ["p2", "Paper 2", 65, "50%"], ["ia", "IA", 30, "25%"]],
  "Marine Science_SL": [["p1", "Paper 1", 65, "50%"], ["p2", "Paper 2", 35, "30%"], ["ia", "IA", 24, "20%"]],
  "Nature of Science_SL": [["p1", "Paper 1", 60, "40%"], ["p2", "Paper 2", 45, "30%"], ["ia", "IA", 28, "30%"]],
  "Physics_SL": [["p1", "Paper 1", 45, "36%"], ["p2", "Paper 2", 50, "44%"], ["ia", "IA.", 24, "20%"]],
  "Physics_HL": [["p1", "Paper 1", 60, "36%"], ["p2", "Paper 2", 90, "44%"], ["ia", "IA", 24, "20%"]],
  "Sports, Exercise and Health Science_SL": [["p1", "Paper 1", 30, "20%"], ["p2", "Paper 2", 50, "35%"], ["p3", "Paper 3", 40, "25%"], ["ia", "IA", 24, "20%"]],
  "Sports, Exercise and Health Science_HL": [["p1", "Paper 1", 40, "20%"], ["p2", "Paper 2", 90, "35%"], ["p3", "Paper 3", 50, "25%"], ["ia", "IA", 24, "20%"]],
};

const normalizeSpaces = (value) => value.replace(/\s+/g, " ").trim();
const normalizeSubject = (value) => normalizeSpaces(value).toLowerCase();

const COMPONENT_SUBJECTS = Object.keys(COMPONENTS).reduce((acc, key) => {
  const match = key.match(/^(.*)_(SL|HL)$/);
  if (!match) return acc;
  const subject = match[1];
  const level = match[2];
  const normalized = normalizeSubject(subject);
  if (!acc[normalized]) acc[normalized] = { subject, levels: new Set() };
  acc[normalized].levels.add(level);
  return acc;
}, {});

const parseGroupFourSubjectKey = (key) => {
  const match = key.match(/^(SL|HL)\s+(.+)$/i);
  if (!match) return null;
  const level = match[1].toUpperCase();
  const rawSubject = normalizeSpaces(match[2]);
  const lookup = COMPONENT_SUBJECTS[normalizeSubject(rawSubject)];
  if (!lookup || !lookup.levels.has(level)) return null;
  return { key, level, subject: lookup.subject };
};

function GroupFour({
  groupId = "4",
  boundary = "m24",
  onBoundaryNotFound,
  headerExtras = null,
  afterTitleExtras = null,
}) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = `groups.${groupId}.groupFour`;
  useFlowDefaults(storagePath, "groupFour", !isLoading);
  const [subject, setSubject] = useCalculatorStoredValue(`${storagePath}.subject`, "");
  const [level, setLevel] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [scores, setScores] = useCalculatorStoredValue(`${storagePath}.scores`, {});
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  useEffect(() => { setBoundaryNotFound(false); }, [boundary]);

  const LEVELS = ["SL", "HL"];

  const parsedSubjects = catalogKeys.map(parseGroupFourSubjectKey).filter(Boolean);

  const levelsBySubject = parsedSubjects.reduce((acc, item) => {
    if (!acc[item.subject]) acc[item.subject] = new Set();
    acc[item.subject].add(item.level);
    return acc;
  }, {});

  const subjectOptions = (
    level
      ? Object.keys(levelsBySubject).filter((s) => levelsBySubject[s]?.has(level))
      : Object.keys(levelsBySubject)
  ).sort((a, b) => a.localeCompare(b));

  const availableLevels = subject
    ? LEVELS.filter((l) => levelsBySubject[subject]?.has(l))
    : [];

  const componentKey = subject && level ? `${subject}_${level}` : null;
  const subjectKeyCandidates =
    subject && level
      ? parsedSubjects.filter((item) => item.subject === subject && item.level === level).map((item) => item.key)
      : [];
  const allSubjectKeyCandidates = Array.from(
    new Set([...subjectKeyCandidates, ...(subject && level ? [`${level} ${subject}`] : [])])
  );

  const scoreFields = useMemo(
    () => (componentKey ? (COMPONENTS[componentKey] || []) : []),
    [componentKey]
  );

  const hasCompleteScores =
    !!componentKey &&
    scoreFields.length > 0 &&
    scoreFields.every(([k]) => scores[k] !== "");

  const boundaryOrder = getBoundaryResolutionOrder(boundary);

  let entry = null;
  let resolvedBoundary = null;
  if (hasCompleteScores) {
    for (const cb of boundaryOrder) {
      const cd = dataByBoundary[cb];
      if (!cd) continue;
      let candidateEntry = null;
      for (const sk of allSubjectKeyCandidates) {
        const found = getEntryForSubject(cd.timezones, sk, tz).entry;
        if (found) { candidateEntry = found; break; }
      }
      if (candidateEntry) { entry = candidateEntry; resolvedBoundary = cb; break; }
    }
  }
  const entryAvailable = Boolean(entry);

  useEffect(() => {
    if (isLoading) return;
    if (!hasCompleteScores) {
      if (boundaryNotFound) setBoundaryNotFound(false);
      return;
    }
    if (!entryAvailable) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else if (boundaryNotFound) {
      setBoundaryNotFound(false);
    }
  }, [isLoading, hasCompleteScores, entryAvailable, boundaryNotFound]);

  let grade = null;
  let neededText = null;
  let pointsAboveBoundary = null;
  if (hasCompleteScores && entry) {
    const total = scoreFields.reduce((sum, [k, , max, wt]) => {
      return sum + (Number(scores[k]) / max) * (parseFloat(wt) / 100) * 100;
    }, 0);
    const cutoffs = Array.from({ length: 7 }, (_, i) => parseFloat(entry[String(i + 1)]));
    for (let i = 6; i >= 0; i--) {
      if (total >= cutoffs[i]) {
        grade = i + 1;
        if (i < 6) {
          neededText = `Need ${(cutoffs[i + 1] - total).toFixed(1)} percentage points for grade ${i + 2}`;
        }
        break;
      }
    }

    if (grade !== null) {
      pointsAboveBoundary = getPointsAboveBoundary(total, entry, grade);
    }
  }

  useEffect(() => {
    if (!componentKey) return;

    setScores((currentScores) => {
      const nextScores = scoreFields.reduce(
        (acc, [k]) => ({ ...acc, [k]: currentScores[k] ?? "" }),
        {}
      );
      const keys = Object.keys(nextScores);
      const currentKeys = Object.keys(currentScores);
      const matchesCurrentScores =
        keys.length === currentKeys.length &&
        keys.every((key) => currentScores[key] === nextScores[key]);

      return matchesCurrentScores ? currentScores : nextScores;
    });
  }, [componentKey, scoreFields, setScores]);

  useEffect(() => {
    if (isLoading) return;

    if (grade !== null && subject && level) {
      updateGrade(groupId, grade, subject, level, neededText);
    } else {
      updateGrade(groupId, null, null, null);
    }
  }, [isLoading, grade, subject, level, neededText, updateGrade, groupId]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;

  if (isLoading) {
    return <div className="calculator-pad"><p>Loading…</p></div>;
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return <div className="calculator-pad"><p>Boundary data unavailable.</p></div>;
  }

  return (
    <FlowSubjectRow groupId={groupId} fields={scoreFields} scores={scores} onScoresChange={setScores} grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} fallbackBoundaryNote={fallbackBoundaryNote} error={boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null} levelControl={<FlowLevelSelect groupId={groupId} value={level} options={availableLevels} onChange={value => { setLevel(value); setScores({}); }} />} choices={<>
      {headerExtras}{afterTitleExtras}
      <FlowChoice label="Subject"><select value={subject} onChange={e => { setSubject(e.target.value); setScores({}); }}><option value="" disabled>Subject</option>{subjectOptions.map(value => <option key={value} value={value}>{value}</option>)}</select></FlowChoice>
    </>} />
  );
}

export default React.memo(GroupFour);
